import React, { useMemo, useState } from 'react';
import { Card, Flex, message, Tag } from 'antd';
import { FaCheckSquare, FaPlus, FaSquare } from 'react-icons/fa';
import Title from 'antd/es/typography/Title';
import DateComponent from '~/components/DateComponent';
import { useCheckAuth } from '~/components/Can/checkAuth';
import EmployeeModal from './modals/employees';
import { teamColumns } from '~/tables/team';
import Button from '~/components/Button';
import { Table } from '~/components/Table';
import Strong from '~/components/Strong';
import { Input } from 'formik-antd';
import FormControl from '~/components/Form/FormControl';
import { FiSend } from 'react-icons/fi';
import api from '~/services/api';
import errorHandler from '~/Utils/errorHandler';
import InputCurrency from '~/components/Form/InputCurrency';

export default function TeamModal({ values, setFieldValue, t, errors, isMobile, eventRevenues }) {
  const authCheck = useCheckAuth();
  const [buttonLoading, setButtonLoading] = useState(false);

  const [employeeVisible, setEmployeeVisible] = useState(false);
  const [sectorId, setSectorId] = useState(null);
  const [sectorIndex, setSectorIndex] = useState(null);
  const [operationalId, setOperationalId] = useState(null);
  const [sectorOperationalIndex, setSectorOperationalIndex] = useState(null);
  const [checked, setChecked] = useState(true);

  const handleEmployeeModal = (sectorId, operationalId, sectorIndex, sectorOperationalIndex) => {
    setSectorId(sectorId);
    setSectorIndex(sectorIndex);
    setOperationalId(operationalId);
    setSectorOperationalIndex(sectorOperationalIndex);
    setEmployeeVisible(true);
  };

  const handleDelete = (index) => {
    setFieldValue(
      'team',
      values.team.filter((_, i) => i !== index)
    );
  };

  const fluidPermissions = () => {
    if (eventRevenues) return !eventRevenues;
    return authCheck('@events/teamEdit');
  };

  const totalStaff = useMemo(() => {
    return (
      values?.sectors?.reduce(
        (acc, sector) => acc + (sector?.sector_operationals?.reduce((sum, op) => sum + (op?.amount || 0), 0) || 0),
        0
      ) || 0
    );
  }, [values?.sectors]);

  const handleCheck = () => {
    setChecked(!checked);
    values?.team?.map((e, i) => {
      setFieldValue(`team.${i}.send_message`, checked);
    });
  };

  const sendMessage = async () => {
    setButtonLoading(true);
    try {
      const phonesToSend = values.team.filter((e) => e.send_message).map((ets) => ets.employee_phone);
      const body = {
        phonesToSend,
        eventName: values?.name,
        message: values?.wpp,
      };

      await api.post('/events/send-rdn-msg', body);

      message.success(t('Mensagens enviadas com sucesso!'));
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setButtonLoading(false);
  };

  return (
    <div>
      <Flex justify="space-between">
        <div>
          <h3 style={{ marginBottom: 10 }}>Horário de saída pré-selecionado</h3>
          <DateComponent
            showTime
            onChange={(date) => setFieldValue('default_departure_at', date)}
            onOk={(value) => {
              values?.team?.map((e, i) => {
                if (!e.departure_at) {
                  setFieldValue(`team.${i}.departure_at`, value);
                }
              });
            }}
            value={values?.default_departure_at}
            disabled={!fluidPermissions()}
          />
        </div>
        <Tag style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Strong>{t('Total colaboradores: ') + totalStaff}</Strong>
        </Tag>
      </Flex>
      <Flex align="center" justify="start" style={{ marginTop: 20 }}>
        <Button icon={!checked ? <FaCheckSquare /> : <FaSquare />} onClick={handleCheck}>
          {t('Marcar/Desmarcar todos')}
        </Button>
      </Flex>
      {values?.sectors?.map((sector, sectorIndex) => (
        <Card
          key={sectorIndex}
          title={
            <Title level={5} style={{ margin: 0 }}>
              {sector.name}
            </Title>
          }
          style={{ marginTop: 20 }}
        >
          {sector?.sector_operationals?.map((sectorOperational, sectorOperationalIndex) => {
            const assignedEmployees =
              values.team
                ?.map((employee, i) => ({ ...employee, originalIndex: i }))
                .filter((employee) => {
                  if (!sector?.id) {
                    return (
                      employee?.sector_index === sectorIndex &&
                      employee?.operational_id === sectorOperational?.operational_id
                    );
                  } else {
                    return (
                      employee?.sector_id === sector?.id &&
                      employee?.operational_id === sectorOperational?.operational_id
                    );
                  }
                }) || [];

            const remainingEmployees = sectorOperational.amount - assignedEmployees.length;

            return (
              <div
                key={sectorOperationalIndex}
                style={{ marginBottom: 20, backgroundColor: 'rgba(46, 57, 75, 0.1)', padding: 10, borderRadius: 10 }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <Flex align="center" gap={12}>
                    <Title level={5} style={{ margin: 0, flex: 1, whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      {sectorOperational.operational_name}
                    </Title>
                    <Tag color={remainingEmployees === 0 ? 'green' : 'red'} style={{ margin: 0 }}>
                      {remainingEmployees === 0 ? (
                        <strong>Todos designados</strong>
                      ) : (
                        <>
                          Restantes: <strong>{remainingEmployees}</strong>
                        </>
                      )}
                    </Tag>
                    <Flex align="center">
                      <Tag style={{ display: 'flex', margin: 0, height: '30px', alignItems: 'center' }}>
                        Paga (convite):
                      </Tag>
                      <FormControl
                        field={`sectors.${sectorIndex}.sector_operationals.${sectorOperationalIndex}.invite_price`}
                        error={
                          errors?.sectors?.[sectorIndex]?.sector_operationals?.[sectorOperationalIndex]?.invite_price
                        }
                        list
                        align="center"
                        justify="center"
                      >
                        <InputCurrency
                          name={`sectors.${sectorIndex}.sector_operationals.${sectorOperationalIndex}.invite_price`}
                          disabled={!fluidPermissions()}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>

                  <Button
                    color="primary"
                    onClick={() =>
                      handleEmployeeModal(
                        sector?.id,
                        sectorOperational?.operational_id,
                        sectorIndex,
                        sectorOperationalIndex
                      )
                    }
                    disabled={remainingEmployees <= 0 || !fluidPermissions()}
                    icon={<FaPlus />}
                  >
                    {t('Adicionar colaborador')}
                  </Button>
                </div>

                <Table
                  key="employee_id"
                  dataSource={assignedEmployees}
                  columns={teamColumns(errors, fluidPermissions, setFieldValue, handleDelete, values?.team)}
                  pagination={false}
                  bordered
                  size="small"
                  empty={false}
                />
              </div>
            );
          })}
        </Card>
      ))}
      <Flex align="center" justify="center" gap={20}>
        <FormControl label={t('Mensagem')} field="wpp" error={errors?.wpp} style={{ marginTop: 20 }}>
          <Input.TextArea name="wpp"></Input.TextArea>
        </FormControl>
        <Button loading={buttonLoading} size="large" style={{ marginTop: 60 }} icon={<FiSend />} onClick={sendMessage}>
          {t('Enviar mensagem aos marcados')}
        </Button>
      </Flex>

      <EmployeeModal
        visible={employeeVisible}
        onClose={() => setEmployeeVisible(false)}
        t={t}
        values={values}
        sectorId={sectorId}
        sectorIndex={sectorIndex}
        operationalId={operationalId}
        sectorOperationalIndex={sectorOperationalIndex}
        setFieldValue={setFieldValue}
        fluidPermissions={fluidPermissions}
        isMobile={isMobile}
      />
    </div>
  );
}
